import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  BusinessUnitReportFilters,
  CompanyReportFilters,
  MerchantReportFilters,
  UserReportFilters,
  OperatorReportFilters,
  DispersionReportFilters,
  DispersionReportVariant,
  DispersionVariantReportFilters,
  SettlementReportFilters,
  TerminalReportFilters,
  TpvReportRepository,
} from '@app/domain/ports/tpv/tpv-report.repository';
import { TransactionListQuery } from '@app/domain/ports/tpv/transaction.repository';
import { TpvReportFile, TpvReportFormat } from '@app/domain/models/tpv/tpv-report.model';
import { TpvReportFileDto } from '@app/data/dtos/tpv/tpv-report.dto';
import { tpvUrl } from '@app/data/tpv/tpv-api.config';
import { TpvEnvelope, unwrapTpv } from '@app/data/tpv/tpv-envelope';
import { toHttpParams } from './tpv-http.util';

/** CSV compacto para filtros multi-valor (igual que el listado de transacciones). */
const csv = (v?: string[]): string | undefined => (v && v.length ? v.join(',') : undefined);

/** Mapea el DTO de `extra_data` al modelo de dominio. */
function mapReportFile(dto: TpvReportFileDto | null | undefined): TpvReportFile {
  return {
    fileName: dto?.file_name?.trim() || 'reporte',
    fileBase64: dto?.file_base64 ?? '',
    fileSize: dto?.file_size ?? 0,
  };
}

/**
 * Reportes descargables TPV (`GET .../report/`). El archivo viaja en `extra_data`
 * del envelope de la casa (contrato §1.2); `unwrapTpv` lo extrae. Auth y x-api-key
 * vía interceptores estándar. El scope lo fuerza el backend (contrato §1.4).
 */
@Injectable()
export class TpvReportRemoteDataSource extends TpvReportRepository {
  private readonly http = inject(HttpClient);

  override getTransactionsReport(
    format: TpvReportFormat,
    externalIdMerchant: string | undefined,
    query: TransactionListQuery,
    breakdown?: boolean,
  ): Observable<TpvReportFile> {
    // Mismas KEYS que el listado (MAYÚSCULAS, multi-valor CSV). Sin id_merchant →
    // todos los comercios del usuario (toHttpParams omite undefined).
    const params = toHttpParams({
      format,
      id_merchant: externalIdMerchant,
      merchant_name: query.merchantName,
      start_date: query.startDate,
      end_date: query.endDate,
      status: csv(query.status),
      payment_method: csv(query.paymentMethod),
      card_brand: csv(query.cardBrand),
      operation_type: csv(query.operationType),
      processor: csv(query.processor),
      id_terminal: csv(query.idTerminal),
      min_amount: query.minAmount,
      max_amount: query.maxAmount,
      approved: query.approved,
      // El correo va TAMBIÉN acá: si el reporte no espeja el filtro del listado, el
      // archivo descargado trae un universo distinto del que el operador ve en pantalla.
      email: query.email,
      // Desglose (comisiones por transacción): el backend lo exige junto con id_merchant.
      breakdown: breakdown ? true : undefined,
    });
    return this.request('transactions/report/', params);
  }

  override getMerchantsReport(
    format: TpvReportFormat,
    filters: MerchantReportFilters,
  ): Observable<TpvReportFile> {
    // Mismas KEYS que `GET /merchants/` (contrato §2.2). toHttpParams omite undefined.
    const params = toHttpParams({
      format,
      merchant_name: filters.merchantName,
      social_name: filters.socialName,
      company_name: filters.companyName,
      entity: csv(filters.entity),
      status: csv(filters.status),
      id_unit: filters.idUnit,
      org_id_company: filters.orgIdCompany,
      id_merchant: csv(filters.idMerchant),
      deleted: filters.deleted,
    });
    return this.request('merchants/report/', params);
  }

  override getCompaniesReport(
    format: TpvReportFormat,
    filters: CompanyReportFilters,
  ): Observable<TpvReportFile> {
    // Mismas KEYS que `GET /companies/` (contrato §2.3).
    const params = toHttpParams({
      format,
      name: filters.name,
      social_name: filters.socialName,
      entity: csv(filters.entity),
      status: csv(filters.status),
      id_company: csv(filters.idCompany),
      deleted: filters.deleted,
    });
    return this.request('companies/report/', params);
  }

  override getBusinessUnitsReport(
    format: TpvReportFormat,
    filters: BusinessUnitReportFilters,
  ): Observable<TpvReportFile> {
    // Mismas KEYS que `GET /business-units/` (contrato §2.4).
    const params = toHttpParams({
      format,
      name: filters.name,
      company_name: filters.companyName,
      social_name: filters.socialName,
      entity: csv(filters.entity),
      status: csv(filters.status),
      id_unit: csv(filters.idUnit),
      id_company: filters.idCompany,
      deleted: filters.deleted,
    });
    return this.request('business-units/report/', params);
  }

  override getUsersReport(
    format: TpvReportFormat,
    filters: UserReportFilters,
  ): Observable<TpvReportFile> {
    const params = toHttpParams({
      format,
      search: filters.search,
      role_name: filters.roleName,
      status: csv(filters.status),
    });
    return this.request('users/report/', params);
  }

  override getOperatorsReport(
    format: TpvReportFormat,
    idCompany: number | string,
    filters: OperatorReportFilters,
  ): Observable<TpvReportFile> {
    const params = toHttpParams({
      format,
      search: filters.search,
      role_name: filters.roleName,
      status: csv(filters.status),
      company_wide: filters.companyWide ? true : undefined,
    });
    return this.request(`companies/${idCompany}/operators/report/`, params);
  }

  override getDispersionsReport(
    format: TpvReportFormat,
    filters: DispersionReportFilters,
  ): Observable<TpvReportFile> {
    const params = toHttpParams({
      format,
      id_merchant: filters.idMerchant,
      merchant_name: filters.merchantName,
      company_name: filters.companyName,
      unit_name: filters.unitName,
      processor: csv(filters.processors),
      status: filters.status,
      days: filters.days,
      min_amount: filters.minAmount,
      max_amount: filters.maxAmount,
      start_date: filters.startDate,
      end_date: filters.endDate,
    });
    return this.request('dispersions/report/', params);
  }

  override getDispersionsVariantReport(
    variant: DispersionReportVariant,
    format: TpvReportFormat,
    filters: DispersionVariantReportFilters,
  ): Observable<TpvReportFile> {
    // Contrato suite: rango por FECHA A LIQUIDAR (settlement_start/end, YYYY-MM-DD);
    // `level` solo aplica a `acumulado` (omitido → derivado del scope).
    // Los 9 filtros al final espejean los del listado de transacciones (historia
    // de usuario Transacciones Aprobadas, 2026-09-30): el backend hoy los IGNORA
    // en `aprobadas` — no rompe — pero se cablean para que el archivo descargado
    // corresponda al mismo universo que el operador ve en pantalla cuando backend
    // habilite la lectura (H3 / TPVWB-938).
    const params = toHttpParams({
      format,
      id_merchant: filters.idMerchant,
      settlement_start: filters.settlementStart,
      settlement_end: filters.settlementEnd,
      company_name: filters.companyName,
      unit_name: filters.unitName,
      level: filters.level,
      status: csv(filters.status),
      payment_method: csv(filters.paymentMethod),
      card_brand: csv(filters.cardBrand),
      operation_type: csv(filters.operationType),
      processor: csv(filters.processor),
      id_terminal: csv(filters.idTerminal),
      email: filters.email,
      min_amount: filters.minAmount,
      max_amount: filters.maxAmount,
    });
    return this.request(`dispersions/report/${variant}/`, params);
  }

  override getSettlementsReport(
    format: TpvReportFormat,
    filters: SettlementReportFilters,
  ): Observable<TpvReportFile> {
    const params = toHttpParams({
      format,
      id_merchant: filters.idMerchant,
      merchant_name: filters.merchantName,
      company_name: filters.companyName,
      unit_name: filters.unitName,
      id_dispersion: filters.idDispersion,
      bank: filters.bank,
      tracking_key: filters.trackingKey,
      min_amount: filters.minAmount,
      max_amount: filters.maxAmount,
      start_date: filters.startDate,
      end_date: filters.endDate,
    });
    return this.request('settlements/report/', params);
  }

  override getTerminalsReport(
    format: TpvReportFormat,
    filters: TerminalReportFilters,
  ): Observable<TpvReportFile> {
    // Mismas KEYS que `GET /terminals/` (contrato §4). toHttpParams omite undefined.
    const params = toHttpParams({
      format,
      id_merchant: filters.idMerchant,
      id_terminal: filters.idTerminal,
      serial_number: filters.serialNumber,
      name: filters.name,
      merchant_name: filters.merchantName,
      company_name: filters.companyName,
      unit_name: filters.unitName,
      status: filters.status,
      version_app: filters.versionApp,
      id_address: filters.idAddress,
    });
    return this.request('terminals/report/', params);
  }

  private request(path: string, params: ReturnType<typeof toHttpParams>): Observable<TpvReportFile> {
    return this.http
      .get<TpvEnvelope<TpvReportFileDto>>(tpvUrl(path), { params })
      .pipe(map((raw) => mapReportFile(unwrapTpv<TpvReportFileDto>(raw))));
  }
}
